import { useState, type FormEvent } from 'react';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { usePortalAuth } from '@/portal/auth.store';

/**
 * Entrada do cliente.
 *
 * A mensagem de recusa é sempre a mesma — "e-mail ou senha inválidos" —, e o
 * servidor faz questão de que seja: distinguir "não existe" de "senha errada"
 * de "esta é a porta errada para você" enumeraria contas, e a última também
 * diria a um atacante quais e-mails são de funcionários da Aptum.
 */
export function LoginPage() {
  const situacao = usePortalAuth((estado) => estado.situacao);
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [codigo, setCodigo] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  const pendente = situacao === 'mfa-pendente';

  async function submeter(evento: FormEvent) {
    evento.preventDefault();
    setErro(null);
    try {
      if (pendente) await usePortalAuth.getState().confirmarMfa(codigo);
      else await usePortalAuth.getState().entrar(email, senha);
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível entrar.');
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-background px-4 py-12">
      <Card className="w-full max-w-sm p-6">
        <h1 className="text-lg font-semibold text-gradient">Central Aptum</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {pendente
            ? 'Informe o código do seu aplicativo autenticador.'
            : 'Portal de atendimento. Entre com o seu e-mail corporativo.'}
        </p>

        <form className="mt-6 space-y-4" onSubmit={(e) => void submeter(e)}>
          {pendente ? (
            <Campo
              rotulo="Código de verificação"
              tipo="text"
              valor={codigo}
              aoMudar={setCodigo}
              autoComplete="one-time-code"
            />
          ) : (
            <>
              <Campo
                rotulo="E-mail"
                tipo="email"
                valor={email}
                aoMudar={setEmail}
                autoComplete="username"
              />
              <Campo
                rotulo="Senha"
                tipo="password"
                valor={senha}
                aoMudar={setSenha}
                autoComplete="current-password"
              />
            </>
          )}

          {erro ? (
            <p role="alert" className="text-sm text-destructive">
              {erro}
            </p>
          ) : null}

          <Button type="submit" className="w-full" loading={situacao === 'entrando'}>
            {pendente ? 'Confirmar' : 'Entrar'}
          </Button>
        </form>
      </Card>
    </div>
  );
}

function Campo({
  rotulo,
  tipo,
  valor,
  aoMudar,
  autoComplete,
}: {
  rotulo: string;
  tipo: string;
  valor: string;
  aoMudar: (v: string) => void;
  autoComplete: string;
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium">{rotulo}</span>
      <input
        type={tipo}
        value={valor}
        required
        autoComplete={autoComplete}
        onChange={(e) => aoMudar(e.target.value)}
        className="mt-1 h-10 w-full rounded-md border border-border bg-background px-3 text-sm"
      />
    </label>
  );
}
